import React, { useEffect, useState } from 'react';
import { Plus, Image, Trash, PencilSimple, X, Check, CaretUp, CaretDown } from '@phosphor-icons/react';
import axios from 'axios';
import { API } from '../../App';

const Portfolio = () => {
  const [portfolioItems, setPortfolioItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    client: '',
    location: '',
    date: '',
    image_url: '',
    gallery_images: []
  });

  useEffect(() => {
    fetchPortfolio();
  }, []);

  const fetchPortfolio = async () => {
    try {
      const response = await axios.get(`${API}/portfolio`, { withCredentials: true });
      setPortfolioItems(response.data);
    } catch (error) {
      console.error('Failed to fetch portfolio:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e, field = 'image_url') => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await axios.post(`${API}/admin/upload`, formDataUpload, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
      
      if (field === 'gallery') {
        setFormData(prev => ({
          ...prev,
          gallery_images: [...prev.gallery_images, imageUrl]
        }));
      } else {
        setFormData(prev => ({ ...prev, image_url: imageUrl }));
      }
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Échec du téléchargement');
    } finally {
      setUploading(false);
    }
  };

  const removeGalleryImage = (index) => {
    setFormData(prev => ({
      ...prev,
      gallery_images: prev.gallery_images.filter((_, i) => i !== index)
    }));
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      client: '',
      location: '',
      date: '',
      image_url: '',
      gallery_images: []
    });
    setEditingItem(null);
    setShowAddModal(false);
  };

  const handleEdit = (item) => {
    setFormData({
      title: item.title || '',
      description: item.description || '',
      client: item.client || '',
      location: item.location || '',
      date: item.date || '',
      image_url: item.image_url || '',
      gallery_images: item.gallery_images || []
    });
    setEditingItem(item);
    setShowAddModal(true);
  };

  const handleSave = async () => {
    if (!formData.title || !formData.image_url) {
      alert('Le titre et l\'image principale sont requis');
      return;
    }

    setSaving(true);
    try {
      if (editingItem) {
        await axios.put(`${API}/admin/portfolio/${editingItem.id}`, formData, {
          withCredentials: true
        });
      } else {
        await axios.post(`${API}/admin/portfolio`, formData, {
          withCredentials: true
        });
      }
      await fetchPortfolio();
      resetForm();
    } catch (error) {
      console.error('Failed to save:', error);
      alert('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette réalisation ?')) return;
    
    try {
      await axios.delete(`${API}/admin/portfolio/${id}`, { withCredentials: true });
      await fetchPortfolio();
    } catch (error) {
      console.error('Failed to delete:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const moveItem = async (index, direction) => {
    const items = [...portfolioItems];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= items.length) return;
    
    [items[index], items[newIndex]] = [items[newIndex], items[index]];
    
    // Update order in backend
    try {
      await axios.put(`${API}/admin/portfolio/reorder`, 
        { ids: items.map(i => i.id) },
        { withCredentials: true }
      );
      setPortfolioItems(items);
    } catch (error) {
      console.error('Failed to reorder:', error);
    }
  };

  return (
    <div data-testid="admin-portfolio">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-3xl text-white mb-2">Portfolio Studio Créa</h1>
          <p className="text-stone-400">Gérez vos réalisations et projets sur mesure</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="btn-primary flex items-center gap-2"
        >
          <Plus size={18} weight="bold" />
          Ajouter une réalisation
        </button>
      </div>

      {/* Portfolio Grid */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="spinner" />
        </div>
      ) : portfolioItems.length === 0 ? (
        <div className="admin-card p-12 text-center">
          <Image size={48} weight="thin" className="mx-auto text-stone-600 mb-4" />
          <h3 className="text-white text-lg mb-2">Aucune réalisation</h3>
          <p className="text-stone-400 mb-6">
            Commencez par ajouter vos projets et réalisations
          </p>
          <button
            onClick={() => setShowAddModal(true)}
            className="btn-secondary"
          >
            Ajouter une réalisation
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {portfolioItems.map((item, index) => (
            <div key={item.id} className="admin-card overflow-hidden group">
              {/* Image */}
              <div className="aspect-[4/3] overflow-hidden bg-black">
                <img
                  src={item.image_url}
                  alt={item.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>

              {/* Content */}
              <div className="p-4">
                <h3 className="font-display text-lg text-white mb-1">{item.title}</h3>
                {item.client && (
                  <p className="text-sm text-stone-400 mb-2">{item.client}</p>
                )}
                {item.description && (
                  <p className="text-sm text-stone-500 line-clamp-2">{item.description}</p>
                )}

                {/* Actions */}
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/5">
                  <div className="flex gap-1">
                    <button
                      onClick={() => moveItem(index, 'up')}
                      disabled={index === 0}
                      className="p-2 text-stone-400 hover:text-white disabled:opacity-30"
                      title="Monter"
                    >
                      <CaretUp size={16} />
                    </button>
                    <button
                      onClick={() => moveItem(index, 'down')}
                      disabled={index === portfolioItems.length - 1}
                      className="p-2 text-stone-400 hover:text-white disabled:opacity-30"
                      title="Descendre"
                    >
                      <CaretDown size={16} />
                    </button>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEdit(item)}
                      className="p-2 text-stone-400 hover:text-white"
                      title="Modifier"
                    >
                      <PencilSimple size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(item.id)}
                      className="p-2 text-stone-400 hover:text-red-400"
                      title="Supprimer"
                    >
                      <Trash size={16} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-6">
          <div className="bg-[#0A0A0A] rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <h2 className="font-display text-xl text-white">
                {editingItem ? 'Modifier la réalisation' : 'Nouvelle réalisation'}
              </h2>
              <button onClick={resetForm} className="text-stone-400 hover:text-white">
                <X size={24} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-6">
              {/* Title */}
              <div>
                <label className="block text-sm text-stone-400 mb-2">Titre *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="admin-input"
                  placeholder="Nom du projet"
                />
              </div>

              {/* Main Image */}
              <div>
                <label className="block text-sm text-stone-400 mb-2">Image principale *</label>
                <div className="flex gap-4">
                  <input
                    type="text"
                    value={formData.image_url}
                    onChange={(e) => setFormData(prev => ({ ...prev, image_url: e.target.value }))}
                    className="admin-input flex-1"
                    placeholder="URL de l'image"
                  />
                  <label className="btn-secondary cursor-pointer flex items-center gap-2">
                    <Image size={18} />
                    {uploading ? '...' : 'Upload'}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handleImageUpload(e, 'image_url')}
                    />
                  </label>
                </div>
                {formData.image_url && (
                  <div className="mt-4 aspect-video w-full max-w-sm overflow-hidden rounded bg-black">
                    <img src={formData.image_url} alt="Preview" className="w-full h-full object-cover" />
                  </div>
                )}
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm text-stone-400 mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="admin-input resize-none"
                  placeholder="Description du projet..."
                />
              </div>

              {/* Client & Location */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-stone-400 mb-2">Client</label>
                  <input
                    type="text"
                    value={formData.client}
                    onChange={(e) => setFormData(prev => ({ ...prev, client: e.target.value }))}
                    className="admin-input"
                    placeholder="Nom du client"
                  />
                </div>
                <div>
                  <label className="block text-sm text-stone-400 mb-2">Lieu</label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                    className="admin-input"
                    placeholder="Paris, France"
                  />
                </div>
              </div>

              {/* Date */}
              <div>
                <label className="block text-sm text-stone-400 mb-2">Date</label>
                <input
                  type="text"
                  value={formData.date}
                  onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                  className="admin-input"
                  placeholder="Mars 2026"
                />
              </div>

              {/* Gallery */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm text-stone-400">Galerie d'images</label>
                  <label className="text-sm text-white/60 hover:text-white cursor-pointer flex items-center gap-1">
                    <Plus size={14} />
                    Ajouter
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handleImageUpload(e, 'gallery')}
                    />
                  </label>
                </div>
                
                {formData.gallery_images.length > 0 ? (
                  <div className="grid grid-cols-4 gap-2">
                    {formData.gallery_images.map((img, index) => (
                      <div key={index} className="relative aspect-square">
                        <img src={img} alt="" className="w-full h-full object-cover rounded" />
                        <button
                          onClick={() => removeGalleryImage(index)}
                          className="absolute top-1 right-1 w-6 h-6 bg-black/80 rounded-full flex items-center justify-center text-white hover:bg-red-500"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-stone-500">
                    Aucune image dans la galerie
                  </p>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end gap-3 p-6 border-t border-white/10">
              <button onClick={resetForm} className="btn-secondary">
                Annuler
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="btn-primary flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    Sauvegarde...
                  </>
                ) : (
                  <>
                    <Check size={18} />
                    {editingItem ? 'Mettre à jour' : 'Créer'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Portfolio;
